/**
 * @file fileParser.js
 * @description Utilities for parsing CSV and XLSX files for bulk QR generation.
 *
 * Responsibilities:
 *  - Validate the file type and size before attempting to parse
 *  - Parse the binary data using SheetJS (xlsx) with safe defaults
 *  - Normalise all cell values to trimmed strings so downstream code
 *    never has to deal with numbers, dates, or undefined coming out of
 *    the spreadsheet
 *  - Auto-detect the EV Number and Chassis Number columns from common
 *    header names found in operations export files
 *  - Filter rows to only those that have non-empty values in both key columns
 */

import * as XLSX from 'xlsx';

// ─── Constants ────────────────────────────────────────────────────────────────

/** File extensions that are accepted for upload. */
export const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'];

/** 50 MB expressed in bytes. */
export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

/**
 * Hard upper limit on the number of data rows.
 * Files with more rows than this will be rejected with an actionable message.
 */
export const MAX_ROW_LIMIT = 10_000;

/**
 * Common column-header fragments used in operations exports to represent the
 * EV Number field. Matching is case-insensitive and checks whether the header
 * *contains* any of these substrings, so "EV_NUMBER", "ev number", "EV No."
 * are all matched.
 */
const EV_NUMBER_HINTS = ['ev number', 'ev_number', 'evnumber', 'ev no', 'ev_no', 'evno', 'ev id', 'ev_id', 'evid'];

/**
 * Common column-header fragments for the Chassis Number field.
 */
const CHASSIS_HINTS = ['chassis number', 'chassis_number', 'chassisnumber', 'chassis no', 'chassis_no', 'chassisno', 'chassis id', 'chassis_id', 'chassisid', 'vin'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extract the lowercase file extension (including the leading dot) from a
 * filename.  Returns an empty string when the name has no extension.
 *
 * @param {string} filename
 * @returns {string}  e.g. ".xlsx"
 */
function getExtension(filename) {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return '';
    return filename.slice(lastDot).toLowerCase();
}

/**
 * Convert any spreadsheet cell value to a safe, trimmed string.
 * Numbers are stringified as-is; undefined / null become empty strings.
 *
 * @param {*} value  Raw cell value from SheetJS
 * @returns {string}
 */
function normaliseCell(value) {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Validate a File object before any parsing is attempted.
 *
 * Checks:
 *  1. The file has an accepted extension (.csv or .xlsx)
 *  2. The file size does not exceed the 50 MB cap
 *
 * @param {File} file  The File object from a file-input or drop event
 * @returns {{ valid: boolean, error: string|null, ext: string }}
 *   `valid` is true when the file can be parsed.
 *   `error` contains a human-readable message when `valid` is false.
 *   `ext`   is the lowercase extension (e.g. ".xlsx") regardless of validity.
 */
export function validateFile(file) {
    if (!file) {
        return { valid: false, error: 'No file provided.', ext: '' };
    }

    const ext = getExtension(file.name);

    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        return {
            valid: false,
            error: `Unsupported file type "${ext || '(none)'}". Please upload a .csv or .xlsx file.`,
            ext,
        };
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(1);
        return {
            valid: false,
            error: `File is too large (${sizeMB} MB). Maximum allowed size is 50 MB.`,
            ext,
        };
    }

    return { valid: true, error: null, ext };
}

/**
 * Read and parse a CSV or XLSX file into a structured result.
 *
 * The function reads the first worksheet only.  All cell values are
 * normalised to trimmed strings so downstream mapping code receives a
 * homogeneous data type.
 *
 * Row-count validation:
 *  - Empty files (zero data rows) are flagged with an error.
 *  - Files exceeding MAX_ROW_LIMIT are flagged with an actionable message.
 *
 * @param {File} file  A validated File object (call validateFile first)
 * @returns {Promise<{
 *   headers: string[],
 *   rows: Array<Record<string, string>>,
 *   totalRows: number,
 *   error: string|null
 * }>}
 */
export async function parseFile(file) {
    const emptyResult = { headers: [], rows: [], totalRows: 0, error: null };

    try {
        // Read the file as an ArrayBuffer so SheetJS works for both CSV and XLSX
        const arrayBuffer = await file.arrayBuffer();

        const workbook = XLSX.read(arrayBuffer, {
            type: 'array',
            // raw:false  → SheetJS formats dates, numbers etc. as strings
            raw: false,
            // defval:''  → missing cells become empty strings, not undefined
            defval: '',
        });

        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
            return { ...emptyResult, error: 'The file contains no worksheets.' };
        }

        const worksheet = workbook.Sheets[firstSheetName];

        // sheet_to_json with header:1 gives us a 2-D array; the first row is headers
        const rawMatrix = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            raw: false,
        });

        if (!rawMatrix || rawMatrix.length === 0) {
            return { ...emptyResult, error: 'The file appears to be empty.' };
        }

        // First row is treated as the header row
        const headerRow = rawMatrix[0];
        const headers = headerRow.map((h) => normaliseCell(h));

        // Remaining rows are data
        const dataRows = rawMatrix.slice(1);

        if (dataRows.length === 0) {
            return {
                headers,
                rows: [],
                totalRows: 0,
                error: 'The file has a header row but no data rows.',
            };
        }

        // Enforce the hard row limit before building the row objects so we
        // do not allocate a massive array only to throw it away
        if (dataRows.length > MAX_ROW_LIMIT) {
            return {
                headers,
                rows: [],
                totalRows: dataRows.length,
                error: `This file contains ${dataRows.length.toLocaleString()} rows, which exceeds the ${MAX_ROW_LIMIT.toLocaleString()}-row limit. Please split the file into smaller batches.`,
            };
        }

        // Convert each data row to a keyed object using the header names
        const rows = dataRows.map((rowArray) => {
            const rowObj = {};
            headers.forEach((header, colIndex) => {
                // Use the normalised header as the key; guard against blank headers
                // by falling back to the column index string
                const key = header || `col_${colIndex}`;
                rowObj[key] = normaliseCell(rowArray[colIndex]);
            });
            return rowObj;
        });

        return { headers, rows, totalRows: rows.length, error: null };
    } catch (err) {
        console.error('[fileParser] parseFile error:', err);
        return {
            ...emptyResult,
            error: `Failed to parse file: ${err?.message || 'Unknown error'}. Please ensure the file is a valid CSV or XLSX.`,
        };
    }
}

/**
 * Filter a parsed row array to only those rows where both the EV Number
 * column and the Chassis Number column contain non-empty values.
 *
 * Rows with at least one missing value are counted as "skipped" so the UI
 * can surface a helpful summary to the user.
 *
 * @param {Array<Record<string, string>>} rows        All data rows from parseFile
 * @param {string}                         evCol      Header name for the EV Number column
 * @param {string}                         chassisCol Header name for the Chassis Number column
 * @returns {{ valid: Array<Record<string, string>>, skippedCount: number }}
 */
export function filterValidRows(rows, evCol, chassisCol) {
    if (!Array.isArray(rows) || !evCol || !chassisCol) {
        return { valid: [], skippedCount: 0 };
    }

    const valid = [];
    let skippedCount = 0;

    for (const row of rows) {
        const evValue = (row[evCol] || '').trim();
        const chassisValue = (row[chassisCol] || '').trim();

        if (evValue && chassisValue) {
            valid.push(row);
        } else {
            skippedCount++;
        }
    }

    return { valid, skippedCount };
}

/**
 * Attempt to auto-detect which column header most likely represents the
 * EV Number field by checking for common substrings (case-insensitive).
 *
 * The function returns the first header that matches any hint, or null if
 * no confident match is found — leaving the user to pick manually.
 *
 * @param {string[]} headers  The header row values from parseFile
 * @returns {string|null}  The matched header string, or null
 */
export function autoDetectEVColumn(headers) {
    if (!Array.isArray(headers)) return null;

    for (const header of headers) {
        const lower = header.toLowerCase();
        if (EV_NUMBER_HINTS.some((hint) => lower.includes(hint))) {
            return header;
        }
    }
    return null;
}

/**
 * Attempt to auto-detect which column header most likely represents the
 * Chassis Number field by checking for common substrings (case-insensitive).
 *
 * Returns the first matching header, or null.
 *
 * @param {string[]} headers  The header row values from parseFile
 * @returns {string|null}  The matched header string, or null
 */
export function autoDetectChassisColumn(headers) {
    if (!Array.isArray(headers)) return null;

    for (const header of headers) {
        const lower = header.toLowerCase();
        if (CHASSIS_HINTS.some((hint) => lower.includes(hint))) {
            return header;
        }
    }
    return null;
}
